import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";

export type ViewAsRole = "auto" | "member" | "premium" | "founding";

interface ViewAsContextType {
  role: ViewAsRole;
  setRole: (r: ViewAsRole) => void;
  isOverridden: boolean;
}

const STORAGE_KEY = "admin-view-as-role";
const ViewAsContext = createContext<ViewAsContextType>({
  role: "auto",
  setRole: () => {},
  isOverridden: false,
});

const read = (): ViewAsRole => {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY);
    if (v === "member" || v === "premium" || v === "founding" || v === "auto") return v;
  } catch {}
  return "auto";
};

export function ViewAsProvider({ children }: { children: ReactNode }) {
  const [role, setRoleState] = useState<ViewAsRole>("auto");

  useEffect(() => {
    setRoleState(read());
    const onStorage = () => setRoleState(read());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setRole = useCallback((r: ViewAsRole) => {
    try {
      if (r === "auto") sessionStorage.removeItem(STORAGE_KEY);
      else sessionStorage.setItem(STORAGE_KEY, r);
    } catch {}
    setRoleState(r);
  }, []);

  return (
    <ViewAsContext.Provider value={{ role, setRole, isOverridden: role !== "auto" }}>
      {children}
    </ViewAsContext.Provider>
  );
}

export function useViewAs() {
  return useContext(ViewAsContext);
}